import { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { FormExpireApiKeyData } from 'dashboard/forms/FormExpireApiKey';
import { FormPasswordConfirmationModalData } from 'dashboard/forms/FormPasswordConfirmationModal';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ApiKey } from 'dashboard/models/api-key';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearApiKey } from 'dashboard/redux/slices/api-key';
import { RootState } from 'dashboard/redux/store';
import { expireApiKey, loadApiKey, updateApiKey } from 'dashboard/redux/thunk-actions/api-key';
import DashboardApiKeysShowView from 'dashboard/views/DashboardApiKeysShowView';
import { ApiPermission } from 'lib/api-permissions';
import { ApiKeyInflection, ApiVersion } from 'lib/constants/api';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

export interface FormUpdateApiKeyData {
  name: string;
  note: string;
  'api-version': ApiVersion;
  'api-key-inflection': ApiKeyInflection;
  'api-attributes-blocklist': string[];
  permissions: ApiPermission[];
  'ip-address-allowlist': string[];
  'file-access-token-expires-in': number;
}

type Params = { apiKeyId: string };
const DashboardApiKeysShowRoute = (props: PropsFromRedux) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const { apiKeyId } = useParams<Params>() as Params; // apiKeyId will always be present
  useOnMountAndUnmount(props.clearApiKey);

  useRedirectOnEnvironmentChange('/dashboard/api-logs');

  const { loading: isLoadingApiKey, callback: loadApiKey } = useApiLoader({
    loader: () => props.loadApiKey(apiKeyId),
    deps: [props.loadApiKey, apiKeyId, props.showMessage],
    onNetworkError: () => props.showMessage('Failed to load API key.', MessageType.Failure),
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load API key.', MessageType.Failure);
      }
    },
  });

  // ==============================
  // Update API Key
  // ==============================

  const { error: updateApiKeyError, callback: handleUpdateApiKey } = useApiCallback(
    async (apiKey: ApiKey, data: FormUpdateApiKeyData, meta: FormPasswordConfirmationModalData) => {
      setIsSuccess(false);
      await props.updateApiKey(
        apiKey.id,
        {
          name: data['name'],
          note: data['note'],
          'api-version': data['api-version'],
          'api-key-inflection': data['api-key-inflection'],
          permissions: data['permissions'],
          'api-attributes-blocklist': data['api-attributes-blocklist'],
          'ip-address-allowlist': data['ip-address-allowlist'],
          'file-access-token-expires-in': data['file-access-token-expires-in'],
        },
        {
          // Note this will be blank if organization has SAML enabled.
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'password-confirmation': meta?.['password-confirmation'],
        },
      );

      loadApiKey();
      setIsSuccess(true);
      props.showMessage('Saved API key', MessageType.Success);
    },
    [props.updateApiKey, props.showMessage, history],
  );

  useApiErrorHandler({
    error: updateApiKeyError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        const details = error.errors?.[0]?.details;
        const errorMessage: string =
          typeof details === 'string' ? details : 'Error encountered when updating API key.';
        props.showMessage(errorMessage, MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  // ==============================
  // Expire API Key
  // ==============================

  const { error: expireApiKeyError, callback: handleExpireApiKey } = useApiCallback(
    async (apiKey: ApiKey, data: FormExpireApiKeyData, meta: FormPasswordConfirmationModalData) => {
      await props.expireApiKey(apiKey.id, {
        'expires-in-seconds': data['expires-in-seconds'],
        // Note this will be blank if organization has SAML enabled.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'password-confirmation': meta?.['password-confirmation'],
      });

      loadApiKey();
      props.showMessage('Expired API key', MessageType.Success);
    },
    [props.expireApiKey, props.showMessage, history],
  );

  useApiErrorHandler({
    error: expireApiKeyError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Error encountered when expiring API key.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  // ==============================
  // Render
  // ==============================

  if (props.apiKey == null) {
    if (isLoadingApiKey) {
      return <LoadingIndicator fullscreen={true} />;
    } else {
      return (
        <Backdrop>
          <NullState message='We couldn’t find that API key.' backRoute='/dashboard/api-keys' />
        </Backdrop>
      );
    }
  }

  return (
    <DashboardApiKeysShowView
      organization={props.organization}
      apiKey={props.apiKey}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleUpdateApiKey={handleUpdateApiKey}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleExpireApiKey={handleExpireApiKey}
      isSuccess={isSuccess}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    organization: state.organization.organization,
    apiKey: state.apiKey.apiKey,
  };
};

const mapDispatchToProps = {
  loadApiKey,
  clearApiKey,
  updateApiKey,
  expireApiKey,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardApiKeysShowRoute);
